import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MembersPage, RolesPage } from '@/modules/iam';
import { useAuth } from '@/auth/AuthProvider';
import { iamApi } from '@/modules/iam/services/iam.api';
import {
  CRYPTO_LOCAL_SEARCH_TEMPORARY_PRESET,
  OUTREACH_TEMPORARY_PRESET,
  SEARCH_ONLY_TEMPORARY_PRESET,
  parseTemporaryPresetFeatures,
  serializeTemporaryPresetFeatures,
  type TemporaryPresetFeature,
} from '@/lib/orgGates';

type Tab = 'members' | 'roles' | 'features';

const BASE_TABS: { id: Tab; label: string }[] = [
  { id: 'members', label: 'Members' },
  { id: 'roles', label: 'Roles' },
];

const FEATURE_OPTIONS: { id: TemporaryPresetFeature; label: string }[] = [
  { id: SEARCH_ONLY_TEMPORARY_PRESET, label: 'Search only' },
  { id: OUTREACH_TEMPORARY_PRESET, label: 'Outreach' },
  { id: CRYPTO_LOCAL_SEARCH_TEMPORARY_PRESET, label: 'Talent Pool Search (local)' },
];

/**
 * Organization settings. The active tab is reflected in the URL via ?tab= so
 * deep links from old paths still work via the redirects registered in App.tsx.
 *
 * The legacy ?tab=invite is folded back into the Members tab — invites are now
 * launched from the "Invite people" button at the top of the member list.
 */
export function OrganizationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isStaff = Boolean(user?.isStaff);
  const rawTab = searchParams.get('tab');
  const tabs = useMemo(
    () => (isStaff ? [...BASE_TABS, { id: 'features' as const, label: 'Features' }] : BASE_TABS),
    [isStaff],
  );
  const tab: Tab = useMemo(() => {
    if (rawTab === 'roles' || rawTab === 'members') return rawTab;
    if (rawTab === 'features' && isStaff) return rawTab;
    return 'members';
  }, [rawTab, isStaff]);

  useEffect(() => {
    // Normalize legacy ?tab=invite → ?tab=members.
    if (rawTab !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params);
  };

  return (
    <div
      className="h-full overflow-y-auto"
      style={{ background: 'var(--canvas-base)' }}
    >
      <div className="px-8 py-10 max-w-5xl mx-auto">
      <header className="mb-8">
        <h1
          className="text-[34px] font-light tracking-[-0.02em] leading-tight"
          style={{ fontFamily: 'var(--v2-font-serif)', color: 'var(--text-primary)' }}
        >
          Organization
        </h1>
        <p className="text-sm mt-1.5" style={{ color: 'var(--text-secondary)' }}>
          Manage members and roles for this organization.
        </p>
      </header>

      <div className="mb-7" style={{ borderBottom: '1px solid var(--border-default)' }}>
        <nav className="-mb-px flex gap-6" aria-label="Organization sections">
          {tabs.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="px-1 py-3 text-sm transition-colors"
                style={{
                  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                  color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  fontWeight: active ? 500 : 400,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        {tab === 'members' && <MembersPage />}
        {tab === 'roles' && <RolesPage />}
        {tab === 'features' && isStaff && <TemporaryPresetPanel />}
      </div>
      </div>
    </div>
  );
}

function TemporaryPresetPanel() {
  const { organization, init } = useAuth();
  const [selected, setSelected] = useState<TemporaryPresetFeature[]>([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const features = parseTemporaryPresetFeatures(organization?.temporaryPreset);
    setSelected(FEATURE_OPTIONS.filter((option) => features.includes(option.id)).map((option) => option.id));
  }, [organization?.temporaryPreset]);

  const toggleFeature = (feature: TemporaryPresetFeature) => {
    setSelected((current) =>
      current.includes(feature) ? current.filter((item) => item !== feature) : [...current, feature],
    );
    setStatus(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    setError(null);
    try {
      await iamApi.updateOrg({ temporaryPreset: serializeTemporaryPresetFeatures(selected) });
      await init();
      setStatus('Saved');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Unable to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="max-w-2xl rounded-lg border px-5 py-5"
      style={{ borderColor: 'var(--border-default)', background: 'var(--surface-primary)' }}
    >
      <div className="mb-5">
        <h2 className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>
          Temporary preset
        </h2>
      </div>

      <div className="space-y-3">
        {FEATURE_OPTIONS.map((option) => (
          <label key={option.id} className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-primary)' }}>
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={() => toggleFeature(option.id)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {status && <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{status}</span>}
        {error && <span className="text-sm" style={{ color: 'var(--fg-danger)' }}>{error}</span>}
      </div>
    </section>
  );
}
